import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  cancelarPlano, getMeuPlano, getPlano, getPreviaDaTroca, IdDoPlano, MeuPlano, SituacaoDoPlano, trocarDePlano,
} from '../services/api';
import { useConfirmar } from '../contexts/Confirmacao';
import { useTravarScroll, useFocoNoModal } from '../utils/travarScroll';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';
import './MeuPlanoModal.css';
import { abrirAbaDePagamento } from '../utils/abaDePagamento';
import { ITENS } from './TelaDePlanos';

/**
 * "Meu plano" (22/10) — `docs/designs/precificacao.md`, "Trocar de plano e cancelar".
 *
 * Ver o plano, trocar e cancelar. As regras moram no servidor; aqui só se diz,
 * ANTES do toque, o que vai acontecer:
 *
 * - subir (ganhar banco) é na hora, pagando a diferença dos dias que faltam;
 * - descer é no vencimento;
 * - cancelar: até o 7º dia, devolve tudo (o teste de 7 dias); depois, vale até o fim;
 *   depois, o acesso vale até o fim, sem devolução.
 */

const reais = (c: number): string => `R$ ${(c / 100).toFixed(2).replace('.', ',')}`;
const data = (iso: string | null): string => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : '—');
const NOMES: Record<IdDoPlano, string> = { essencial: 'Essencial', essencial_anual: 'Anual', completo: 'Completo' };
const ORDEM: IdDoPlano[] = ['essencial', 'completo', 'essencial_anual'];

const diasDesde = (iso: string): number =>
  Math.round((Date.now() - Date.parse(`${iso}T12:00:00`)) / 86_400_000);

/*
 * O plano, guardado por conta (22/10), como Newsletter e Compartilhar: o modal
 * abre com o que já veio e confere em silêncio; o menu do avatar já busca
 * antes. Trocar e cancelar continuam perguntando ao servidor na hora.
 */
type Lido = { m: Awaited<ReturnType<typeof getMeuPlano>>; t: SituacaoDoPlano };
const guardados = new Map<string, Lido>();
const buscando = new Map<string, Promise<Lido>>();

export const buscarMeuPlano = (userId = ''): Promise<Lido> => {
  const emCurso = buscando.get(userId);
  if (emCurso) return emCurso;
  const pedido = Promise.all([getMeuPlano(), getPlano()])
    .then(([m, t]) => { const r = { m, t }; guardados.set(userId, r); return r; })
    .finally(() => buscando.delete(userId));
  buscando.set(userId, pedido);
  return pedido;
};

const MeuPlanoModal: React.FC<{ onClose: () => void; userId?: string }> = ({ onClose, userId = '' }) => {
  const jaVeio = guardados.get(userId);
  const [meu, setMeu] = useState<MeuPlano | null>(jaVeio?.m.plano ?? null);
  const [podeTrocar, setPodeTrocar] = useState(jaVeio?.m.podeTrocar ?? false);
  const [tabela, setTabela] = useState<SituacaoDoPlano | null>(jaVeio?.t ?? null);
  const [carregando, setCarregando] = useState(!jaVeio);
  const [agindo, setAgindo] = useState(false);
  const [recado, setRecado] = useState<string | null>(null);
  const [comparando, setComparando] = useState(false);
  const caixa = useRef<HTMLDivElement>(null);
  const corpo = useRef<HTMLDivElement>(null);
  const confirmar = useConfirmar();

  useTravarScroll();
  useFocoNoModal(caixa);
  useFechavelComVoltar(true, onClose);

  // Só a primeira leitura mostra "Carregando...". As de depois (após trocar,
  // ao voltar da aba do pagamento) atualizam por baixo: esconder tudo fazia o
  // modal "piscar" no celular e o aviso parecer sumir (22/10).
  const primeira = useRef(!jaVeio);
  const carregar = useCallback(async () => {
    if (primeira.current) setCarregando(true);
    try {
      const { m, t } = await buscarMeuPlano(userId);
      setMeu(m.plano); setPodeTrocar(m.podeTrocar); setTabela(t);
    } catch {
      setRecado('Não consegui ler o seu plano agora. Tente de novo em instantes.');
    } finally {
      primeira.current = false;
      setCarregando(false);
    }
  }, [userId]);
  useEffect(() => { void carregar(); }, [carregar]);
  // Chegou um aviso: o corpo rola até ele.
  useEffect(() => { if (recado) corpo.current?.scrollTo({ top: 0, behavior: 'smooth' }); }, [recado]);
  // Voltou da aba do pagamento: lê de novo, para o plano novo aparecer.
  useEffect(() => {
    const aoVoltar = () => { if (document.visibilityState === 'visible') void carregar(); };
    document.addEventListener('visibilitychange', aoVoltar);
    return () => document.removeEventListener('visibilitychange', aoVoltar);
  }, [carregar]);

  const dadosDe = (id: IdDoPlano) => tabela?.planos.find(x => x.id === id);
  /** Bancos incluídos no plano (28/10: 3 no Completo; o Essencial e o Anual não têm banco). */
  const incluidos = (id: IdDoPlano): number => dadosDe(id)?.bancos ?? 1;
  const teto = tabela?.maxBancos ?? 5;
  const preco = (id: IdDoPlano, bancos = 0): number => {
    const p = dadosDe(id);
    if (!p) return 0;
    return p.precoCentavos + (p.aceitaExtra ? Math.max(0, bancos - p.bancos) * (tabela?.bancoExtraCentavos ?? 0) : 0);
  };

  const nomeComBancos = (plano: IdDoPlano, bancos: number): string =>
    plano === 'completo' ? `Completo com ${bancos} ${bancos === 1 ? 'banco' : 'bancos'}` : NOMES[plano];
  const periodo = (plano: IdDoPlano): string => (plano === 'essencial_anual' ? 'por ano' : 'por mês');

  /**
   * Antes de trocar, a confirmação diz o que acontece, quando e quanto custa —
   * com os números do servidor, os mesmos que ele vai cobrar (22/10: ela tocou
   * no plano e a troca já aconteceu, sem dizer nada antes).
   */
  const trocar = async (plano: IdDoPlano, bancos: number) => {
    if (!meu) return;
    setAgindo(true); setRecado(null);
    let previa;
    try {
      previa = await getPreviaDaTroca(plano, bancos);
    } catch (e: any) {
      setRecado(e?.response?.data?.error ?? 'Não consegui calcular a troca agora.');
      setAgindo(false);
      return;
    }
    setAgindo(false);
    if (previa.tipo === 'recusado') {
      setRecado(previa.motivo === 'tem-mais-conectado'
        ? 'Você tem mais bancos conectados do que isso. Desconecte um no Open Finance antes.'
        : previa.motivo === 'cancelado' ? 'A assinatura foi cancelada: assine de novo no fim do período.'
        : previa.motivo === 'renovar-antes' ? 'O seu plano venceu: renove primeiro, e depois troque.'
        : 'Não deu para trocar agora.');
      return;
    }
    const alvo = nomeComBancos(plano, bancos);
    const texto = previa.tipo === 'subida'
      ? (previa.cobra
        ? `Você paga ${reais(previa.valorCentavos)} agora, a diferença até ${data(previa.ate)}, e passa para o ${alvo} hoje. A partir de ${data(previa.ate)}, ${reais(previa.precoDepoisCentavos)} ${periodo(plano)}.`
        : `Você passa para o ${alvo} hoje, sem pagar nada agora. A partir de ${data(previa.ate)}, ${reais(previa.precoDepoisCentavos)} ${periodo(plano)}.`)
      : previa.tipo === 'descida'
        ? `Seu plano muda para o ${alvo} em ${data(previa.quando)}. Até lá, segue como está. A partir de ${data(previa.quando)}, ${reais(previa.precoDepoisCentavos)} ${periodo(plano)}.`
        : `Você continua no plano de agora${meu.planoProximo || meu.maxBancosProximo ? ', e a troca marcada para o vencimento é desfeita' : ''}.`;
    if (!await confirmar({
      titulo: previa.tipo === 'mesmo-plano' ? 'Manter o plano atual?' : `Trocar para o ${alvo}?`,
      texto,
      acao: previa.tipo === 'subida' && previa.cobra ? 'Pagar e trocar' : previa.tipo === 'mesmo-plano' ? 'Manter' : 'Trocar',
      desistir: 'Voltar',
    })) return;
    setAgindo(true);
    // A aba nasce aqui, ainda no clique de confirmar (senão o navegador bloqueia).
    const aba = previa.tipo === 'subida' && previa.cobra ? abrirAbaDePagamento() : null;
    try {
      const r = await trocarDePlano(plano, bancos);
      if (r.tipo === 'link') {
        if (aba) aba.ir(r.link); else window.location.assign(r.link);
        setRecado(`O pagamento de ${reais(r.valorCentavos)} abriu em outra aba. Quando ele for confirmado, o seu plano muda para o ${NOMES[plano]}.`);
        return;
      }
      aba?.fechar();
      if (r.tipo === 'subiu') setRecado(`Pronto: você está no ${NOMES[plano]}.`);
      if (r.tipo === 'no-vencimento') setRecado(`Combinado: muda para o ${NOMES[plano]}${plano === 'completo' ? ` com ${bancos} ${bancos === 1 ? 'banco' : 'bancos'}` : ''} em ${data(r.quando)}. Até lá, segue como está.`);
      if (r.tipo === 'mesmo-plano') setRecado('Você continua no plano de agora.');
      if (r.tipo === 'recusado') {
        setRecado(r.motivo === 'tem-mais-conectado'
          ? 'Você tem mais bancos conectados do que isso. Desconecte um no Open Finance antes.'
          : r.motivo === 'cancelado' ? 'A assinatura foi cancelada: assine de novo no fim do período.'
          : r.motivo === 'renovar-antes' ? 'O seu plano venceu: renove primeiro, e depois troque.'
          : 'Não deu para trocar agora.');
      }
      await carregar();
    } catch (e: any) {
      aba?.fechar();
      setRecado(e?.response?.data?.error ?? 'Não consegui trocar agora. Tente de novo em instantes.');
    } finally {
      setAgindo(false);
    }
  };

  const cancelar = async () => {
    if (!meu) return;
    const ultimo = meu.ultimoPagamento;
    const dias = ultimo ? diasDesde(ultimo.pagoEm) : 99;
    const texto = ultimo && dias <= 7
      ? (ultimo.origem === 'asaas'
        ? `Como faz ${dias} ${dias === 1 ? 'dia' : 'dias'} do pagamento, devolvemos ${reais(ultimo.valorCentavos)} e o acesso termina agora.`
        : 'Como faz menos de 7 dias do pagamento, você recebe tudo de volta: a equipe faz a devolução pelo Pix e avisa por e-mail.')
      : `O acesso vale até ${data(meu.liberadoAte)} e não renova mais.`;
    if (!await confirmar({
      titulo: 'Cancelar a assinatura?',
      alvo: `Plano ${NOMES[meu.plano]}`,
      texto: `${texto} O que você lançou continua guardado.`,
      acao: 'Cancelar assinatura',
      desistir: 'Manter assinatura',
      perigo: true,
    })) return;
    setAgindo(true); setRecado(null);
    try {
      const r = await cancelarPlano();
      setRecado(r.tipo === 'estornado' ? `Cancelado. Devolvemos ${reais(r.centavos)}.`
        : r.tipo === 'pedido-de-devolucao' ? `Cancelado. O acesso vale até ${data(r.valeAte)}, e a equipe responde o pedido de devolução por e-mail.`
        : r.tipo === 'cancelado' ? `Cancelado. O acesso vale até ${data(r.valeAte)}.`
        : 'Essa assinatura já estava cancelada.');
      await carregar();
    } catch (e: any) {
      setRecado(e?.response?.data?.error ?? 'Não consegui cancelar agora. Tente de novo em instantes.');
    } finally {
      setAgindo(false);
    }
  };

  const ativo = meu && meu.estado === 'liberado';
  const proximoPlano = meu?.planoProximo ?? meu?.plano;
  const proximosBancos = meu?.maxBancosProximo ?? meu?.maxBancos ?? 1;

  return createPortal(
    <div className="mp-overlay" onClick={onClose} role="presentation" ref={caixa}>
      <div className="mp-modal" onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="mp-titulo">
        <div className="mp-banda">
          <h2 id="mp-titulo">Meu plano</h2>
          <button type="button" className="mp-fechar" onClick={onClose} aria-label="Fechar">✕</button>
        </div>
        <div className="mp-corpo" ref={corpo}>
          {/* O aviso do que aconteceu vem no TOPO: no fim do modal, no celular, ficava fora da tela. */}
          {recado && <p className="mp-recado" role="status">{recado}</p>}
          {carregando && <p className="mp-nota">Carregando...</p>}
          {!carregando && !ativo && (
            <p className="mp-nota">Você não tem um plano ativo agora.</p>
          )}
          {!carregando && meu && ativo && (
            <>
              <section className="mp-atual">
                <p className="mp-rotulo">Plano atual</p>
                <p className="mp-plano">
                  {NOMES[meu.plano]}
                  {meu.plano === 'completo' && <span> · {meu.maxBancos} {meu.maxBancos === 1 ? 'banco' : 'bancos'}</span>}
                  {meu.fundador && <span className="mp-selo">Fundador</span>}
                </p>
                <p className="mp-nota">
                  {meu.canceladoEm
                    ? <>Cancelado. Vale até <strong>{data(meu.liberadoAte)}</strong>.</>
                    : <>Renova em <strong>{data(meu.liberadoAte)}</strong>{proximoPlano && tabela ? <>, por {reais(preco(proximoPlano, proximosBancos))}</> : null}.</>}
                </p>
                {!meu.canceladoEm && (meu.planoProximo || meu.maxBancosProximo) && proximoPlano && (
                  <p className="mp-nota mp-aviso">
                    Muda para o {NOMES[proximoPlano]}{proximoPlano === 'completo' ? ` com ${proximosBancos} ${proximosBancos === 1 ? 'banco' : 'bancos'}` : ''} no vencimento.
                  </p>
                )}
              </section>

              {!meu.canceladoEm && podeTrocar && (
                <section className="mp-trocar" aria-label="Trocar de plano">
                  <p className="mp-rotulo">Trocar de plano</p>
                  <div className="mp-opcoes">
                    {ORDEM.map(id => {
                      const atual = id === meu.plano;
                      return (
                        <button key={id} type="button" className={`mp-opcao${atual ? ' mp-opcao-atual' : ''}`}
                          disabled={agindo || (atual && !meu.planoProximo && !meu.maxBancosProximo)}
                          onClick={() => trocar(id, Math.max(1, id === meu.plano ? meu.maxBancos : incluidos(id)))}>
                          <strong>{NOMES[id]}</strong>
                          <span>{reais(preco(id))}{id === 'essencial_anual' ? '/ano' : '/mês'}</span>
                          <em>{atual ? 'Seu plano' : id === 'completo' && meu.plano !== 'completo' ? 'Muda agora' : 'No vencimento'}</em>
                        </button>
                      );
                    })}
                  </div>
                  {/* O que cada plano inclui, a mesma lista da tela de planos (22/10: para trocar, ela queria ver as vantagens). */}
                  <button type="button" className="mp-comparar" aria-expanded={comparando} onClick={() => setComparando(c => !c)}>
                    {comparando ? 'Esconder o que cada plano inclui' : 'Ver o que cada plano inclui'}
                  </button>
                  {comparando && (
                    <div className="mp-vantagens">
                      {ORDEM.map(id => (
                        <div key={id} className={`mp-vantagem${id === meu.plano ? ' mp-vantagem-atual' : ''}`}>
                          <p className="mp-vantagem-nome">
                            {NOMES[id]} <span>{reais(preco(id))}{id === 'essencial_anual' ? '/ano' : '/mês'}</span>
                            {id === meu.plano && <em>Seu plano</em>}
                          </p>
                          <ul>
                            {id !== 'essencial' && <li><strong>Tudo do Essencial</strong>{id === 'completo' ? ', e mais:' : ''}</li>}
                            {ITENS[id].map(t => <li key={t}>{t}</li>)}
                            {id === 'completo' && <li>Banco a mais: {reais(tabela?.bancoExtraCentavos ?? 0)}/mês cada</li>}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                  <p className="mp-nota">
                    Para o Completo, a mudança é na hora: você paga só a diferença dos dias que faltam até {data(meu.liberadoAte)}.
                    Para um plano menor, muda no vencimento.
                  </p>
                  {meu.plano === 'completo' && (
                    <div className="mp-bancos">
                      <span>Bancos: <strong>{meu.maxBancos}</strong> de {teto} ({meu.bancosConectados} conectado{meu.bancosConectados === 1 ? '' : 's'}). {incluidos('completo')} já vêm no plano.</span>
                      <button type="button" disabled={agindo || meu.maxBancos >= teto} onClick={() => trocar('completo', meu.maxBancos + 1)}>
                        + 1 banco ({reais(tabela?.bancoExtraCentavos ?? 0)}/mês)
                      </button>
                      <button type="button" disabled={agindo || meu.maxBancos <= incluidos('completo')} onClick={() => trocar('completo', meu.maxBancos - 1)}>
                        − 1 banco
                      </button>
                    </div>
                  )}
                  {/* 28/10: o banco é do Completo; quem está no Essencial ou no Anual vê quanto custa. */}
                  {meu.plano !== 'completo' && (
                    <p className="mp-nota">
                      Quer conectar seus bancos? O Completo tem {incluidos('completo')} por {reais(preco('completo'))}/mês, e cada banco a mais custa {reais(tabela?.bancoExtraCentavos ?? 0)}/mês, até {teto}.
                    </p>
                  )}
                </section>
              )}

              {!meu.canceladoEm && podeTrocar && (
                <button type="button" className="mp-cancelar" disabled={agindo} onClick={cancelar}>Cancelar assinatura</button>
              )}
              {!podeTrocar && <p className="mp-nota">Trocar e cancelar por aqui ainda não está no ar. Escreva para fintracking.info@gmail.com.</p>}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default MeuPlanoModal;
